import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Badge, Button, Form } from '@syneto/compass-react';
import {
	NO_RECIPIENT_MATCHES_MESSAGE,
	NO_RECIPIENT_SELECTED_MESSAGE,
	RECIPIENT_LIST_LEGEND,
	filterRecipients,
	formatRequestedAccessBadge,
	getSelectedCountText,
	hasBulkSelection,
	hasRecipientSearch,
	recipientShape
} from '../../../../../../helpers/partnerPreviewRecipients';

const LIST_MAX_HEIGHT = '240px';
const COUNTER_ID = 'partnerPreviewRecipientCount';

const renderRequestedAccessBadge = (recipient) => {
	const badgeText = formatRequestedAccessBadge(recipient.requestedAccessAt);

	return badgeText ? <Badge appearance="info">{badgeText}</Badge> : null;
};

/*
 * The pick-list itself: only rendered once there is at least one recipient (the modal handles loading, failure
 * and an empty company). It owns the search text, which starts empty again whenever the list is hidden by
 * clearing Notify customer; the selection, held by the modal, survives that. The search box shows only from 5
 * recipients, so with fewer the search text stays empty.
 */
const PartnerPreviewRecipientList = ({
	recipients,
	selectedEmails,
	showSelectionRequired,
	onToggleRecipient,
	onSelectRecipients,
	onClearRecipients
}) => {
	const [searchText, setSearchText] = useState('');

	const selectedEmailSet = new Set(selectedEmails);
	const visibleRecipients = filterRecipients(recipients, searchText);
	const visibleEmails = visibleRecipients.map((recipient) => {
		return recipient.email;
	});

	return (
		<fieldset className="mt-2 ms-4" aria-describedby={COUNTER_ID}>
			<legend className="fs-6 mb-1">{RECIPIENT_LIST_LEGEND}</legend>

			{hasRecipientSearch(recipients) && (
				<Form.Control
					type="search"
					placeholder="Search name or email"
					aria-label="Search recipients by name or email"
					value={searchText}
					onChange={(event) => {
						return setSearchText(event.target.value);
					}}
				/>
			)}

			{hasBulkSelection(recipients) && (
				<div className="mt-2">
					<Button role="tertiary" onClick={() => {
						return onSelectRecipients(visibleEmails);
					}}>Select all</Button>
					<span> / </span>
					<Button role="tertiary" onClick={() => {
						return onClearRecipients(visibleEmails);
					}}>Clear all</Button>
				</div>
			)}

			<div className="mt-2" style={{ maxHeight: LIST_MAX_HEIGHT, overflowY: 'auto' }}>
				{visibleRecipients.length === 0 && <p className="text-muted">{NO_RECIPIENT_MATCHES_MESSAGE}</p>}
				{visibleRecipients.map((recipient) => {
					return (
						<Form.Check
							key={recipient.email}
							type="checkbox"
							id={`partnerPreviewRecipient-${recipient.email}`}
							checked={selectedEmailSet.has(recipient.email)}
							onChange={() => {
								return onToggleRecipient(recipient.email);
							}}
							label={
								<span>
									{recipient.name && <b className="me-2">{recipient.name}</b>}
									<span className="me-2">{recipient.email}</span>
									<span className="text-muted me-2">{recipient.roles.join(', ')}</span>
									{renderRequestedAccessBadge(recipient)}
								</span>
							}
						/>
					);
				})}
			</div>

			<p id={COUNTER_ID} className="mt-2 mb-0" aria-live="polite">{getSelectedCountText(selectedEmails.length)}</p>
			{showSelectionRequired && (
				<p className="text-danger mb-0" role="alert">{NO_RECIPIENT_SELECTED_MESSAGE}</p>
			)}
		</fieldset>
	);
};

export default PartnerPreviewRecipientList;

PartnerPreviewRecipientList.propTypes = {
	recipients: PropTypes.arrayOf(recipientShape).isRequired,
	selectedEmails: PropTypes.arrayOf(PropTypes.string).isRequired,
	showSelectionRequired: PropTypes.bool.isRequired,
	onToggleRecipient: PropTypes.func.isRequired,
	onSelectRecipients: PropTypes.func.isRequired,
	onClearRecipients: PropTypes.func.isRequired
};
